import { useMemo, useState } from "react";
import Topbar from "../components/Topbar";
import FiltersSection from "./analyticsDashboard/components/FiltersSection";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import BestPriceOtaScopeTable from "./analyticsDashboard/components/BestPriceOtaScopeTable";
import { DEFAULT_FILTERS } from "./analyticsDashboard/constants";
import {
  TEST_V1_CHECK_IN_RANGE,
  TEST_V1_FILTER_OPTIONS,
  TEST_V1_HOTELS,
  TEST_V1_RESPONSE,
} from "./analyticsDashboard/testDataV1";

export default function AnalyticsDashboardV1TestPage() {
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    hotel: TEST_V1_HOTELS[0]?.value ?? "",
    date: TEST_V1_CHECK_IN_RANGE.from,
    dateIntervalFrom: TEST_V1_CHECK_IN_RANGE.from,
    dateIntervalTo: TEST_V1_CHECK_IN_RANGE.to,
  });
  const [daysStay, setDaysStay] = useState<1 | 3 | 5>(1);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);

  const selectedHotels = useMemo(() => {
    if (!filters.competitor.length) return TEST_V1_RESPONSE;
    const selected = new Set(filters.competitor);
    return TEST_V1_RESPONSE.filter((hotel) => selected.has(hotel.hotel_name));
  }, [filters.competitor]);

  const handleApply = () => {
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };

  const handleReset = () => {
    setFilters((prev) => ({
      ...DEFAULT_FILTERS,
      hotel: prev.hotel,
      date: TEST_V1_CHECK_IN_RANGE.from,
      dateIntervalFrom: TEST_V1_CHECK_IN_RANGE.from,
      dateIntervalTo: TEST_V1_CHECK_IN_RANGE.to,
    }));
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-56 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 via-violet-100 to-white blur-3xl opacity-80" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-indigo-600">BI-дашборд · тест v1</p>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Название OTA по лучшей цене</h1>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-3 self-start sm:self-auto">
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                  <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                    {[
                      { value: 1 as const, label: "1 ночь" },
                      { value: 3 as const, label: "3 ночи" },
                      { value: 5 as const, label: "5 ночей" },
                    ].map((option) => {
                      const isActive = daysStay === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setDaysStay(option.value);
                            setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
                          }}
                          className={[
                            "rounded-full px-3 py-1 text-xs font-semibold transition",
                            isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                          ].join(" ")}
                          aria-pressed={isActive}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {appliedAt
                    ? `Фильтры обновлены в ${appliedAt}`
                    : "Выберите объект, задайте фильтры и нажмите «Применить»"}
                </div>
              </div>
            </div>
          </section>

          <ObjectSelectCard
            value={filters.hotel}
            options={TEST_V1_HOTELS}
            onChange={(value) => setFilters((prev) => ({ ...prev, hotel: value }))}
          />

          <FiltersSection
            filters={filters}
            options={TEST_V1_FILTER_OPTIONS}
            onChange={setFilters}
            onApply={handleApply}
            onReset={handleReset}
            checkInRange={TEST_V1_CHECK_IN_RANGE}
            showCompetitors
            showDateInterval
          />

          <BestPriceOtaScopeTable
            title="Сравнение OTA по мин. цене"
            subtitle="Платформы и даты отображаются из данных так же, как они будут приходить с backend."
            data={selectedHotels}
          />
        </div>
      </main>
    </div>
  );
}
